import { useEffect, useState, useCallback } from "react";
import { PhoneCall, ChevronDown, Lock, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export const CALL_OUTCOMES = [
  ["not_contacted", "Not Contacted"],
  ["contact_attempted", "Contact Attempted"],
  ["customer_contacted", "Customer Contacted"],
  ["follow_up_required", "Follow-Up Required"],
  ["quote_requested", "Quote Requested"],
  ["work_booked", "Work Booked"],
  ["customer_not_ready", "Customer Not Ready"],
  ["referred_to_aware", "Referred Back to AWARE"],
];

function Section({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 text-left">
        <span className="text-sm font-bold text-slate-800">{title}</span>
        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 py-3 text-sm text-slate-700 leading-relaxed space-y-2">{children}</div>}
    </div>
  );
}

const Say = ({ children }) => (
  <p className="border-l-2 border-cyan-300 pl-3 italic text-slate-700">{children}</p>
);
const Ul = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
);

// Sep 2026 — Customer Results Follow-Up Call Guide. Only available once at
// least one of the partner's customer reports has been approved and
// published; outcomes are recorded (auditable) via /partner/call-outcomes.
export function ResultsCallScriptCard() {
  const [bookings, setBookings] = useState([]);
  const [selRef, setSelRef] = useState("");
  const [outcome, setOutcome] = useState("customer_contacted");
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadRecent = useCallback(() => {
    api.get("/partner/call-outcomes?limit=6").then(({ data }) => setRecent(data.rows || [])).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/partner/bookings?limit=50")
      .then(({ data }) => setBookings(data.rows || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
    loadRecent();
  }, [loadRecent]);

  const hasPublished = bookings.some((b) => b.report_available);

  const record = async () => {
    setBusy(true);
    try {
      await api.post("/partner/call-outcomes", {
        outcome,
        booking_reference: selRef || null,
        note: note || null,
        follow_up_date: followUp || null,
      });
      toast.success("Call outcome recorded");
      setNote(""); setFollowUp("");
      loadRecent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record outcome");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="test-hub-results-call-script-card"
      className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6">
      <div className="flex items-center gap-2">
        <PhoneCall className="w-5 h-5 text-cyan-700" />
        <h2 className="font-display text-lg font-bold text-slate-900">Customer Results Follow-Up Call Guide</h2>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        A simple, non-sales-focused guide for contacting customers after their AWARE water-test
        results have been published.
      </p>

      {loaded && !hasPublished ? (
        <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4 flex items-start gap-3"
          data-testid="test-hub-rcs-locked">
          <Lock className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
          <p className="text-sm text-slate-600">
            This guide becomes available once one of your customers&apos; reports has been
            <b> approved and published</b>. You&apos;ll then be able to follow the call guide and
            record follow-up outcomes here.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-4 space-y-2" data-testid="test-hub-rcs-sections">
            <Section title="Before calling" defaultOpen>
              <Ul items={[
                "Read the customer-facing Water Health Summary.",
                "Review the findings and published recommendations.",
                "Use the published Summary as the source of truth.",
                "Contact customers with flagged findings within one business day where possible.",
                "Never alter, contradict or independently reinterpret laboratory results.",
              ]} />
            </Section>

            <Section title="Recommended opening">
              <Say>&ldquo;G&apos;day [Customer], it&apos;s [Partner Name] from Pristine Water Systems &ndash; [Territory].
                We&apos;re the local service partner for AWARE Water Testing. I&apos;m following up regarding the
                recent water test for your property at [address]. Have you received your results and had a
                chance to look through the summary yet?&rdquo;</Say>
            </Section>

            <Section title="If the customer has read the report">
              <Say>&ldquo;Great. Was there anything in the report you wanted me to explain, or would you like me to
                quickly run through the main findings and recommendations with you?&rdquo;</Say>
            </Section>

            <Section title="If the customer has not read the report">
              <Say>&ldquo;No worries. AWARE has sent the report through by email. I can give you a quick overview of
                the main findings and the recommended next steps now, if that would be helpful.&rdquo;</Say>
            </Section>

            <Section title="Discussing the findings">
              <Say>&ldquo;Your test identified [plain-English finding], and the report recommends [published
                recommendation]. We can help you locally with that if you&apos;d like, but the main reason for my
                call is to make sure you understand the result and know what the next step is.&rdquo;</Say>
            </Section>

            <Section title="If you cannot answer a question">
              <Say>&ldquo;That&apos;s a good question, but I don&apos;t want to guess or give you information that isn&apos;t
                supported by the report. I&apos;ll check it with AWARE Water Testing and arrange for the correct
                information to be provided.&rdquo;</Say>
            </Section>

            <Section title="Closing the call">
              <Say>&ldquo;Would you like me to arrange the recommended work for you, or would you prefer some time to
                go through the report first?&rdquo;</Say>
            </Section>

            <Section title="Partner rules — must / must not">
              <p className="font-semibold text-emerald-800">Partners must:</p>
              <Ul items={[
                "Follow the published Summary and recommendations.",
                "Use calm, factual and plain-English language.",
                "Clearly distinguish between essential, recommended and optional actions.",
                "Record the call outcome and any required follow-up.",
              ]} />
              <p className="font-semibold text-rose-800 mt-2">Partners must not:</p>
              <Ul items={[
                "State that water is \u201csafe\u201d or \u201cunsafe\u201d unless that exact approved wording appears in the published report.",
                "Provide medical advice or health guarantees.",
                "Use exaggerated or fear-based language.",
                "Recommend products or services unrelated to the published findings.",
                "Present BioCleanse as a replacement for tank cleaning, source correction, UV protection or another primary recommendation.",
                "Debate or reinterpret laboratory results.",
                "Discuss results with anyone who does not have permission to receive them.",
              ]} />
            </Section>
          </div>

          {/* ── Outcome logging ── */}
          <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4" data-testid="test-hub-rcs-outcomes">
            <div className="text-sm font-bold text-slate-800">Record the call outcome</div>
            <div className="mt-2 grid sm:grid-cols-2 gap-2">
              <select data-testid="test-hub-rcs-booking-select" value={selRef}
                onChange={(e) => setSelRef(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
                <option value="">No specific booking (general)</option>
                {bookings.map((b) => {
                  const label = `${b.reference} — ${(b.customer && b.customer.name) || "Customer"}${b.report_available ? " · report published" : ""}`;
                  return <option key={b.reference} value={b.reference}>{label}</option>;
                })}
              </select>
              <select data-testid="test-hub-rcs-outcome-select" value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
                {CALL_OUTCOMES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </div>
            <div className="mt-2 grid sm:grid-cols-[1fr_auto] gap-2">
              <input data-testid="test-hub-rcs-note" value={note} maxLength={500}
                onChange={(e) => setNote(e.target.value)} placeholder="Short note (optional)"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
              <label className="flex flex-col gap-1 text-[11px] font-semibold text-slate-500">
                Follow-up date (optional)
                <input data-testid="test-hub-rcs-followup-date" type="date" value={followUp}
                  onChange={(e) => setFollowUp(e.target.value)}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-600" />
              </label>
            </div>
            <button type="button" disabled={busy} onClick={record}
              data-testid="test-hub-rcs-record-btn"
              className="mt-3 inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-bold disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />}
              Record outcome
            </button>

            {recent.length > 0 && (
              <div className="mt-4" data-testid="test-hub-rcs-recent">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Recently recorded</div>
                <ul className="space-y-1 text-xs text-slate-600">
                  {recent.map((r) => (
                    <li key={r.id} className="flex flex-wrap gap-1.5">
                      <span className="font-semibold text-slate-800">{r.outcome_label}</span>
                      {r.booking_reference && <span>· {r.booking_reference}</span>}
                      {r.customer_name && <span>· {r.customer_name}</span>}
                      {r.follow_up_date && <span className="text-amber-700">· follow up {r.follow_up_date}</span>}
                      {r.note && <span className="text-slate-500">· “{r.note}”</span>}
                      <span className="text-slate-400">· {new Date(r.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

export default ResultsCallScriptCard;
